import { z } from 'zod';
import { SkillTriggerKindSchema } from '../admin/agent-skill-definitions.schemas';

/**
 * A skill run's lifecycle, mirroring the IMM-307 `AgentRun` statuses it inherits
 * — a skill run is an `AgentRun`, so it stops and fails for exactly the same
 * reasons and shows the same limit messages.
 */
export const SKILL_RUN_STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED', 'STOPPED'] as const;

export const SkillRunStatusSchema = z.enum(SKILL_RUN_STATUSES);

export type SkillRunStatus = (typeof SKILL_RUN_STATUSES)[number];

/** Why a run was cancelled before it ever reached the model. */
export const SKILL_RUN_CANCELLATION_REASONS = ['REVOKED'] as const;

export const SkillRunCancellationReasonSchema = z.enum(SKILL_RUN_CANCELLATION_REASONS);

export type SkillRunCancellationReason = (typeof SKILL_RUN_CANCELLATION_REASONS)[number];

/**
 * What became of one tool dispatch.
 *
 * `REFUSED` is not an error the tool raised — it is the run engine declining to
 * dispatch a call outside the effective toolset, recorded so the refusal is
 * auditable rather than invisible.
 *
 * `PROPOSED` is the same shape of fact for an `approval_required` command
 * (IMM-436.5): the run reached the call, froze it, and stopped. It is recorded
 * as a tool call because that is what it was — the run's terminal act — and
 * leaving it out would make a run that proposed look like a run that did
 * nothing. The execution, if the member approves, is appended later as its own
 * `OK`/`ERROR` row.
 */
export const SKILL_TOOL_CALL_OUTCOMES = ['OK', 'ERROR', 'REFUSED', 'PROPOSED'] as const;

export const SkillToolCallOutcomeSchema = z.enum(SKILL_TOOL_CALL_OUTCOMES);

export type SkillToolCallOutcome = (typeof SKILL_TOOL_CALL_OUTCOMES)[number];

export const SkillRunToolCallSchema = z.object({
  sequence: z.number().int(),
  toolId: z.string().min(1),
  // Nullable because IMM-436.9 redacts recorded parameters after 90 days; the
  // call itself stays in the record, only its inputs go.
  parameters: z.record(z.string(), z.unknown()).nullable(),
  // What the tool answered, verbatim — the reason a member reads when a call
  // failed. Without it a run history can say a skill acted and that the call
  // failed, but never why, which is not an audit trail for an action taken on
  // someone's behalf. `null` for a PROPOSED call (nothing ran yet) and after
  // the same 90-day redaction that clears `parameters`.
  result: z.string().nullable(),
  outcome: SkillToolCallOutcomeSchema,
  latencyMs: z.number().int(),
  tokenCost: z.number().nullable(),
  createdAt: z.iso.datetime(),
});

export type SkillRunToolCall = z.infer<typeof SkillRunToolCallSchema>;

/** One row of an instance's run history. */
export const SkillRunSummarySchema = z.object({
  id: z.uuid(),
  agentRunId: z.uuid(),
  skillInstanceId: z.uuid(),
  triggerSource: SkillTriggerKindSchema,
  triggerReference: z.string().nullable(),
  status: SkillRunStatusSchema,
  // The IMM-307 reason string for a STOPPED or FAILED run — which limit tripped,
  // or what went wrong.
  failureReason: z.string().nullable(),
  cancellationReason: SkillRunCancellationReasonSchema.nullable(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  cost: z.number().nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
});

export type SkillRunSummary = z.infer<typeof SkillRunSummarySchema>;

export const ListSkillRunsResponseSchema = z.object({
  data: z.array(SkillRunSummarySchema),
});

export type ListSkillRunsResponse = z.infer<typeof ListSkillRunsResponseSchema>;

/**
 * The full record of what a run did: its steps, every tool call with parameters
 * and outcome, and the fingerprint of the ordered sequence. The proposed-actions
 * slot IMM-436.5 fills is deliberately absent until it exists.
 */
export const SkillRunDetailSchema = SkillRunSummarySchema.extend({
  stepLog: z.array(z.string()),
  fingerprint: z.string().nullable(),
  toolCalls: z.array(SkillRunToolCallSchema),
});

export type SkillRunDetail = z.infer<typeof SkillRunDetailSchema>;

/** One entry of an instance's continuity state (AC 210). */
export const SkillInstanceStateEntrySchema = z.object({
  key: z.string().min(1),
  value: z.string(),
  updatedAt: z.iso.datetime(),
});

export type SkillInstanceStateEntry = z.infer<typeof SkillInstanceStateEntrySchema>;

export const GetSkillInstanceStateResponseSchema = z.object({
  data: z.array(SkillInstanceStateEntrySchema),
});

export type GetSkillInstanceStateResponse = z.infer<typeof GetSkillInstanceStateResponseSchema>;

export const SkillRunPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
  runId: z.uuid(),
});

/** A manual trigger carries no fields — the instance and the actor are the input. */
export const TriggerSkillRunBodySchema = z.object({}).strict();
